import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Calculator, CheckCircle2, Copy } from "lucide-react";
import { Button } from "../../../ui/Button";
import { QuotationClosingStep } from "./QuotationClosingStep";
import { QuotationPixModal } from "./QuotationPixModal";
import { CrossTenantLinkModal } from "./CrossTenantLinkModal";
import { QuotationOperationContextStore } from "../services/quotationOperationContext";
import { useNotifications } from "../../../feedback/NotificationProvider";
import { TokenService } from "../../../../lib/tokenService";
import { sendToTerminal } from "../../../../lib/terminalLogger";
import { LogCategory } from "../../../../config/logging";
import type { Cliente } from "../../../../types/gerArquivos";
import type { QuotationSummaryPageProps } from "./summary/QuotationSummary.types";
import {
	buildQuotationSummaryText,
	calculateCardPaymentText,
	getPaginatedObjects,
} from "./summary/QuotationSummary.utils";
import {
	QuotationCalculationCard,
	QuotationClientSection,
	QuotationObjectsSection,
	QuotationProductHeader,
	QuotationSummaryFooter,
} from "./summary/QuotationSummarySections";
import { useQuotationClientState } from "./summary/useQuotationClientState";
import { useQuotationCalculationState } from "./summary/useQuotationCalculationState";
import { useQuotationClosingFlow } from "./summary/useQuotationClosingFlow";
import { derivePaymentTemplateGroup } from "../services/paymentFlowKind";
import { useQuotationSummaryTemplate } from "./summary/useQuotationTemplate";

export function QuotationSummaryPage({
	objects,
	selectedRule,
	selectedQuality,
	savingRule,
	routedClient,
	onBack,
	onNewQuotation,
	onOrderCreated,
	onSaveNowRequested,
}: QuotationSummaryPageProps) {
	const { notify } = useNotifications();
	const [copiedFeedback, setCopiedFeedback] = useState(false);
	const [showObjects, setShowObjects] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [itemsPerPage, setItemsPerPage] = useState(3);
	const [isCompactLayout, setIsCompactLayout] = useState(() => {
		if (typeof window === "undefined") return false;
		return window.innerHeight <= 920 || window.innerWidth <= 430;
	});

	useEffect(() => {
		if (typeof window === "undefined") return;
		const updateCompactMode = () => {
			setIsCompactLayout(
				window.innerHeight <= 920 || window.innerWidth <= 430,
			);
		};
		updateCompactMode();
		window.addEventListener("resize", updateCompactMode);
		return () => window.removeEventListener("resize", updateCompactMode);
	}, []);

	if (!objects || objects.length === 0) {
		sendToTerminal(
			"[QUOTATION] Nenhum objeto coletado",
			{},
			LogCategory.ERROR,
		);
		return (
			<div className="fixed inset-0 bg-navy-900 flex flex-col items-center justify-center p-4 z-50">
				<div className="text-center space-y-4">
					<p className="text-red-400 text-lg font-semibold">
						Erro: Nenhum objeto coletado
					</p>
					<p className="text-gray-400">
						Por favor, selecione objetos no CorelDRAW e tente novamente.
					</p>
					<Button onClick={onBack}>Voltar</Button>
				</div>
			</div>
		);
	}

	const clientState = useQuotationClientState({
		routedMode: Boolean(routedClient?.isRouted),
		initialClient: routedClient ?? null,
	});

	const calculationState = useQuotationCalculationState({
		objects,
		selectedRule,
		selectedQuality,
		savingRule,
		selectedClient: clientState.selectedClient,
		manualClientName: clientState.manualClientName,
		notify,
	});

	const closingFlow = useQuotationClosingFlow({
		objects,
		selectedClient: clientState.selectedClient,
		finalClientName: clientState.finalClientName,
		selectedRule,
		selectedQuality,
		savingRule,
		quotationResult: calculationState.quotationResult,
		descontoAplicado: calculationState.descontoAplicado,
		finalMetragem: calculationState.finalMetragem,
		isMetros: calculationState.isMetros,
		totalMetragem: calculationState.totalMetragem,
		totalUnidades: calculationState.totalUnidades,
		notify,
		onOrderCreated,
		onSaveNowRequested,
	});

	const paymentFlow = useMemo(
		() => derivePaymentTemplateGroup(closingFlow.selectedPaymentMethod),
		[closingFlow.selectedPaymentMethod],
	);
	const { templateBody } = useQuotationSummaryTemplate({
		categoriaItem: selectedRule.produto?.categoria_item ?? null,
		paymentFlow,
	});

	const buildSummaryMessage = useCallback(
		(templateOverride?: string | null) => {
			if (!calculationState.quotationResult) return null;

			const clienteNome = clientState.finalClientName || "Cliente nao informado";
			const produtoNome = selectedRule.produto?.nome_servico || "Material";
			const qualidadeNome = selectedQuality?.one1_nome || "";
			const metragemFinal =
				calculationState.finalMetragem !== null
					? calculationState.finalMetragem
					: calculationState.isMetros
						? calculationState.totalMetragem
						: calculationState.totalUnidades;
			const metragemDisplay = calculationState.isMetros
				? metragemFinal.toFixed(2)
				: `${metragemFinal}`;
			const paymentOptions = calculateCardPaymentText(
				calculationState.valorTotalFinal,
			);
			const valorVista = calculationState.valorTotalFinal.toFixed(2);
			const userData = TokenService.getUserData();
			const atendenteNome = userData?.data?.usuario?.nome || "Atendente";
			const dataAtual = new Date().toLocaleDateString("pt-BR");

			return buildQuotationSummaryText(
				{
					clienteNome,
					produtoNome,
					qualidadeNome,
					atendenteNome,
					dataAtual,
					metragemDisplay,
					valorVista,
					paymentOptions,
				},
				templateOverride ?? undefined,
			);
		},
		[
			calculationState.finalMetragem,
			calculationState.isMetros,
			calculationState.quotationResult,
			calculationState.totalMetragem,
			calculationState.totalUnidades,
			calculationState.valorTotalFinal,
			clientState.finalClientName,
			selectedQuality,
			selectedRule,
		],
	);

	// Wizard: passo 1 = resumo do orçamento, passo 2 = fechamento do pedido.
	const [step, setStep] = useState<1 | 2>(1);

	// Gate cross-tenant: quando cliente roteado, o vendedor precisa vincular o
	// pedido de origem (oneuniformes) ANTES de criar o pedido no destino.
	const [showLinkModal, setShowLinkModal] = useState(false);
	const [pendingRoutedCreate, setPendingRoutedCreate] = useState(false);

	// Ao registrar (createdOrderData disponível), avança para o fechamento.
	useEffect(() => {
		if (closingFlow.createdOrderData) setStep(2);
	}, [closingFlow.createdOrderData]);

	// Após confirmar o vínculo de origem no modal, dispara a criação do pedido.
	// Depende de sourceLink já estar setado (handleCreateOrder recriado com o
	// valor novo) — evita closure obsoleta ao chamar logo após setSourceLink.
	useEffect(() => {
		if (
			pendingRoutedCreate &&
			closingFlow.sourceLink.length > 0 &&
			!closingFlow.createdOrderData
		) {
			setPendingRoutedCreate(false);
			void closingFlow.handleCreateOrder();
		}
	}, [
		pendingRoutedCreate,
		closingFlow.sourceLink,
		closingFlow.createdOrderData,
		closingFlow.handleCreateOrder,
	]);

	const handleSelectClient = useCallback(
		(client: Cliente) => {
			closingFlow.resetClosingDetailsPreservingPayment();
			clientState.handleSelectClient(client);
		},
		[clientState, closingFlow],
	);

	const handleClearClient = useCallback(() => {
		closingFlow.resetClosingDetailsPreservingPayment();
		clientState.handleClearClient();
	}, [clientState, closingFlow]);

	const handleToggleManualName = useCallback(
		(enabled: boolean) => {
			closingFlow.resetClosingDetailsPreservingPayment();
			clientState.handleToggleManualName(enabled);
		},
		[clientState, closingFlow],
	);

	const handleToggleRoutedMode = useCallback(
		(enabled: boolean) => {
			closingFlow.resetClosingDetailsPreservingPayment();
			clientState.handleToggleRoutedMode(enabled);
		},
		[clientState, closingFlow],
	);

	const pagination = useMemo(
		() => getPaginatedObjects(objects, currentPage, itemsPerPage),
		[objects, currentPage, itemsPerPage],
	);

	const canCreateOrderFromPageState = useMemo(
		() =>
			!!calculationState.quotationResult &&
			!!clientState.selectedClient?.id &&
			!closingFlow.isCreatingOrder,
		[
			calculationState.quotationResult,
			clientState.selectedClient?.id,
			closingFlow.isCreatingOrder,
		],
	);

	const handleCopyBaseSummary = useCallback(() => {
		if (!calculationState.quotationResult) {
			sendToTerminal(
				"[QUOTATION] Tentativa de copiar sem resultado calculado",
				{},
				LogCategory.ERROR,
			);
			notify({
				type: "warning",
				title: "Atencao",
				message: "Nenhum orcamento calculado para copiar",
				duration: 3000,
			});
			return;
		}

		const summary = buildSummaryMessage();
		if (!summary) return;

		navigator.clipboard
			.writeText(summary)
			.then(() => {
				sendToTerminal(
					"[QUOTATION] Resumo copiado para area de transferencia",
					{
						clienteNome: clientState.finalClientName || "Cliente nao informado",
						produtoNome: selectedRule.produto?.nome_servico || "Material",
						valorTotal: calculationState.valorTotalFinal,
						metragem:
							calculationState.finalMetragem !== null
								? calculationState.finalMetragem
								: calculationState.isMetros
									? calculationState.totalMetragem
									: calculationState.totalUnidades,
					},
					LogCategory.BUSINESS,
				);

				setCopiedFeedback(true);
				setTimeout(() => setCopiedFeedback(false), 2000);

				notify({
					type: "success",
					title: "Copiado!",
					message: "Orcamento base copiado para area de transferencia",
					duration: 3000,
				});
			})
			.catch((err) => {
				const errorMessage =
					err instanceof Error ? err.message : "Erro desconhecido ao copiar";
				sendToTerminal(
					"[QUOTATION] Erro ao copiar para area de transferencia",
					{ error: errorMessage },
					LogCategory.ERROR,
				);

				notify({
					type: "error",
					title: "Erro ao copiar",
					message:
						"Nao foi possivel copiar o orcamento base. Tente novamente.",
					duration: 4000,
				});
			});
	}, [
		buildSummaryMessage,
		calculationState.finalMetragem,
		calculationState.isMetros,
		calculationState.quotationResult,
		calculationState.totalMetragem,
		calculationState.totalUnidades,
		calculationState.valorTotalFinal,
		clientState.finalClientName,
		notify,
		selectedRule,
	]);

	const handleCopyFinalMessage = useCallback(() => {
		if (!closingFlow.selectedPaymentMethodId || !paymentFlow) {
			notify({
				type: "warning",
				title: "Forma de pagamento obrigatoria",
				message:
					"Selecione a forma de pagamento antes de copiar a mensagem final.",
				duration: 3000,
			});
			return;
		}

		const summary = buildSummaryMessage(templateBody);
		if (!summary) {
			notify({
				type: "warning",
				title: "Atencao",
				message: "Nenhum orcamento calculado para copiar.",
				duration: 3000,
			});
			return;
		}

		navigator.clipboard
			.writeText(summary)
			.then(() => {
				notify({
					type: "success",
					title: "Copiado!",
					message: "Mensagem final copiada para area de transferencia",
					duration: 3000,
				});
			})
			.catch((err) => {
				const errorMessage =
					err instanceof Error ? err.message : "Erro desconhecido ao copiar";
				sendToTerminal(
					"[QUOTATION] Erro ao copiar mensagem final",
					{ error: errorMessage },
					LogCategory.ERROR,
				);
				notify({
					type: "error",
					title: "Erro ao copiar",
					message:
						"Nao foi possivel copiar a mensagem final. Tente novamente.",
					duration: 4000,
				});
			});
	}, [
		buildSummaryMessage,
		closingFlow.selectedPaymentMethodId,
		notify,
		paymentFlow,
		templateBody,
	]);

	return (
		<div className="fixed inset-0 bg-navy-900 flex flex-col min-w-[285px] w-full max-w-full z-50">
			<div className={`${isCompactLayout ? "px-2.5 py-2" : "p-2 sm:p-3 md:p-4 lg:p-5"} flex items-center gap-2 border-b border-navy-700 bg-navy-900/50 flex-shrink-0`}>
				<button
					type="button"
					onClick={step === 2 ? () => setStep(1) : onBack}
					className="p-1.5 text-gray-400 hover:text-white transition-colors flex-shrink-0"
					title={step === 2 ? "Voltar ao resumo" : "Voltar"}
					aria-label={step === 2 ? "Voltar ao resumo" : "Voltar"}
				>
					<ArrowLeft className={`${isCompactLayout ? "w-4 h-4" : "w-4 h-4 sm:w-5 sm:h-5"}`} />
				</button>
				<Calculator className={`${isCompactLayout ? "w-4 h-4" : "w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6"} text-blue-400 flex-shrink-0`} />
				<h1 className={`${isCompactLayout ? "text-sm" : "text-sm sm:text-base md:text-lg lg:text-xl"} font-semibold text-white truncate flex-1 min-w-0`}>
					{step === 1 ? "Resumo do Orcamento" : "Fechamento do Pedido"}
				</h1>
			</div>

			{step === 1 ? (
				<>
					<div className={`flex-1 min-h-0 overflow-y-auto ${isCompactLayout ? "p-2 space-y-1.5" : "p-2 sm:p-3 md:p-4 lg:p-5 space-y-2 sm:space-y-3 md:space-y-4"}`}>
						<QuotationClientSection
							compact={isCompactLayout}
							useManualClientName={clientState.useManualClientName}
							selectedClient={clientState.selectedClient}
							clientSearchQuery={clientState.clientSearchQuery}
							manualClientName={clientState.manualClientName}
							carregandoClientes={clientState.carregandoClientes}
							clientes={clientState.clientes}
							routedMode={clientState.routedMode}
							routingError={clientState.routingError}
							preparingRouting={clientState.preparingRouting}
							onClientSearchChange={clientState.setClientSearchQuery}
							onManualClientNameChange={clientState.setManualClientName}
							onToggleManualName={handleToggleManualName}
							onToggleRoutedMode={handleToggleRoutedMode}
							onSelectClient={handleSelectClient}
							onClearClient={handleClearClient}
						/>

						<QuotationProductHeader
							compact={isCompactLayout}
							productName={selectedRule.produto?.nome_servico || "Nao informado"}
							selectedQuality={selectedQuality}
							isMetros={calculationState.isMetros}
						/>

						<QuotationCalculationCard
							compact={isCompactLayout}
							isCalculating={calculationState.isCalculating}
							error={calculationState.error}
							quotationResult={calculationState.quotationResult}
							fonteCalculo={calculationState.fonteCalculo}
							valorTotalFinal={calculationState.valorTotalFinal}
							precoFinal={calculationState.precoFinal}
							isMetros={calculationState.isMetros}
							totalMetragem={calculationState.totalMetragem}
							totalUnidades={calculationState.totalUnidades}
							finalMetragem={calculationState.finalMetragem}
							isEditingMetragem={calculationState.isEditingMetragem}
							editedMetragemString={calculationState.editedMetragemString}
							editedMetragem={calculationState.editedMetragem}
							confirmedMetragem={calculationState.confirmedMetragem}
							minMetragem={calculationState.minMetragem}
							descontoAplicado={calculationState.descontoAplicado}
							onEditedMetragemChange={calculationState.handleEditedMetragemChange}
							onEditedMetragemBlur={calculationState.handleEditedMetragemBlur}
							onToggleEditingMetragem={calculationState.handleToggleEditingMetragem}
							onConfirmEditedMetragem={calculationState.handleConfirmEditedMetragem}
						/>

						<QuotationObjectsSection
							compact={isCompactLayout}
							objects={objects}
							showObjects={showObjects}
							paginatedObjects={pagination.paginatedObjects}
							isMetros={calculationState.isMetros}
							currentPage={currentPage}
							totalPages={pagination.totalPages}
							itemsPerPage={itemsPerPage}
							indexOfFirstItem={pagination.indexOfFirstItem}
							indexOfLastItem={pagination.indexOfLastItem}
							onToggleShowObjects={setShowObjects}
							onChangeItemsPerPage={(value) => {
								setItemsPerPage(value);
								setCurrentPage(1);
							}}
							onChangePage={setCurrentPage}
						/>
					</div>

					<QuotationSummaryFooter
						compact={isCompactLayout}
						quotationResult={calculationState.quotationResult}
						copiedFeedback={copiedFeedback}
						isCreatingOrder={closingFlow.isCreatingOrder}
						hasSelectedClient={!!clientState.selectedClient}
						canCreateOrder={canCreateOrderFromPageState}
						onCopyBaseSummary={handleCopyBaseSummary}
						onCreateOrder={() => {
							if (closingFlow.createdOrderData) {
								setStep(2);
								return;
							}
							// Cliente roteado sem vínculo de origem confirmado: abre o gate
							// obrigatório antes de criar o pedido no destino.
							if (
								QuotationOperationContextStore.isActive() &&
								closingFlow.sourceLink.length === 0
							) {
								setShowLinkModal(true);
								return;
							}
							void closingFlow.handleCreateOrder();
						}}
						onNewQuotation={onNewQuotation}
					/>
				</>
			) : (

				<QuotationClosingStep
					compact={isCompactLayout}
					orderData={
						closingFlow.createdOrderData
							? {
								orcId: closingFlow.createdOrderData.orcId,
								oitId: closingFlow.createdOrderData.oitId,
							}
							: null
					}
					closingState={closingFlow.closingState}
					observacaoPedido={closingFlow.closingForm.observacaoPedido}
					entrega={closingFlow.closingForm.entrega}
					exibirRecibo={closingFlow.closingForm.exibirRecibo}
					precisaNotaFiscal={closingFlow.closingForm.precisaNotaFiscal}
					temTransportadora={closingFlow.closingForm.temTransportadora}
					comprovanteFiles={closingFlow.closingForm.comprovanteFiles}
					createError={closingFlow.createError}
					closingDataError={closingFlow.closingDataError}
					pixError={closingFlow.pixError}
					paymentMethods={closingFlow.paymentMethods}
					selectedPaymentMethodId={closingFlow.selectedPaymentMethodId}
					isLoadingPaymentMethods={closingFlow.isLoadingPaymentMethods}
					paymentMethodsError={closingFlow.paymentMethodsError}
					paymentMethodHint={closingFlow.paymentMethodHint}
					isSavingClosingData={closingFlow.isSavingClosingData}
					isQuickSaving={closingFlow.isQuickSaving}
					isUploadingComprovante={closingFlow.isUploadingComprovante}
					isGeneratingPix={closingFlow.isGeneratingPix}
					isProcessingFinancial={closingFlow.isProcessingFinancial}
					comprovanteStatus={closingFlow.comprovanteStatus}
					comprovanteError={closingFlow.comprovanteError}
					comprovanteUploadedCount={closingFlow.comprovanteUploadedCount}
					canGeneratePix={closingFlow.canGeneratePix}
					canSaveClosing={closingFlow.canSaveClosing}
					hasGeneratedPix={closingFlow.hasGeneratedPix}
					saveClosingHint={closingFlow.saveClosingHint}
					generatePixHint={closingFlow.generatePixHint}
					canProcessFinancial={closingFlow.canProcessFinancial}
					canQuickSave={closingFlow.canQuickSave}
					hasSavedClosingData={closingFlow.hasSavedClosingData}
					isPixPaymentMethodSelected={closingFlow.isPixPaymentMethodSelected}
					quickSaveHint={closingFlow.saveNowHint ?? undefined}
					closingChecklist={closingFlow.closingChecklist}
					closingStatusLabel={closingFlow.closingStatusLabel}
					showComprovanteSection={closingFlow.showComprovanteSection}
					comprovanteRequired={closingFlow.comprovanteRequired}
					comprovanteSectionTooltip={closingFlow.comprovanteSectionTooltip}
					onSelectPaymentMethod={closingFlow.handleSelectPaymentMethod}
					onObservacaoPedidoChange={(value) => closingFlow.setClosingField("observacaoPedido", value)}
					onEntregaChange={(value) => closingFlow.setClosingField("entrega", value)}
					onExibirReciboChange={(value) => closingFlow.setClosingField("exibirRecibo", value)}
					onPrecisaNotaFiscalChange={(value) => closingFlow.setClosingField("precisaNotaFiscal", value)}
					onTemTransportadoraChange={(value) => closingFlow.setClosingField("temTransportadora", value)}
					onComprovanteFilesChange={(files) => closingFlow.setClosingField("comprovanteFiles", files)}
					onRemoveComprovanteFile={(index) => closingFlow.removeClosingFile("comprovanteFiles", index)}
					onRetryComprovanteUpload={() => void closingFlow.handleRetryComprovante()}
					onSaveClosingData={() => void closingFlow.handleSaveClosingData()}
					onGeneratePix={() => void closingFlow.handleGeneratePix()}
					onViewPix={() => closingFlow.setIsPixModalOpen(true)}
					onProcessFinancial={() => void closingFlow.handleProcessFinancialApproval()}
					onQuickSave={() => void closingFlow.handleQuickSaveToGerenciador()}
					onCopyFinalMessage={handleCopyFinalMessage}
					canCopyFinalMessage={
						!!calculationState.quotationResult &&
						!!closingFlow.selectedPaymentMethodId &&
						!!paymentFlow
					}
				/>
			)}

			<QuotationPixModal
				isOpen={closingFlow.isPixModalOpen}
				onClose={() => closingFlow.setIsPixModalOpen(false)}
				onBackToClosing={() => {
					closingFlow.setIsPixModalOpen(false);
					setStep(2);
				}}
				orderNumber={closingFlow.createdOrderData?.orcId ?? null}
				pixData={closingFlow.pixData}
				pixQrImageUrl={closingFlow.pixQrImageUrl}
				pixCopiedFeedback={closingFlow.pixCopiedFeedback}
				onCopyPixCode={closingFlow.handleCopyPixCode}
			/>

			<CrossTenantLinkModal
				isOpen={showLinkModal}
				sourceTenantLabel={
					QuotationOperationContextStore.get()?.sourceTenantId ?? null
				}
				onCancel={() => setShowLinkModal(false)}
				onConfirm={(sources) => {
					closingFlow.setSourceLink(sources);
					setShowLinkModal(false);
					setPendingRoutedCreate(true);
				}}
			/>
		</div>
	);
}
